<template>
  <v-content>
    <v-container my-10>
      <v-layout align-center justify-center>
        <v-progress-circular
          :size="55"
          :width="4"
          indeterminate
          color="primary"
          class="mt-12"
          v-if="loading"
        ></v-progress-circular>
        <v-card v-else max-width="650px" width="650px">
          <v-card-title class="ml-4">
            <span class="headline">{{
              $t("tasks.tasksDetails.task", {
                taskId: editedItem.task_identifier,
              })
            }}</span>
            <v-btn
              v-if="canUseKnowledgeBaseBtn"
              @click="handleHelp"
              icon
              color="primary"
            >
              <v-icon class="mx-2">mdi-help-circle</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <v-card-actions>
              <v-btn text color="primary" @click="edit()"
                ><v-icon small class="mx-2">mdi-pencil</v-icon
                >{{ $t("tasks.tasksDetails.edit") }}
              </v-btn>
              <v-btn
                text
                color="red"
                class="mr-1"
                @click="activateDeleteDialog()"
              >
                <v-icon small class="mx-2">mdi-delete</v-icon
                >{{ $t("tasks.tasksDetails.delete") }}</v-btn
              >
            </v-card-actions>
          </v-card-title>
          <v-card-text class="pb-0">
            <v-row no-gutters>
              <v-col cols="12">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>{{
                      $t("tasks.tasksDetails.title")
                    }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ editedItem.name }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="6">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>{{
                      $t("tasks.tasksDetails.status")
                    }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ getStatus }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
              <v-col cols="6">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>{{
                      $t("tasks.tasksDetails.dueDate")
                    }}</v-list-item-title>
                    <v-list-item-subtitle>
                      <span v-if="editedItem.due_date">
                        {{
                          editedItem.due_date.slice(0, 10)
                            | moment("DD.MM.YYYY")
                        }}</span
                      >
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="6">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>{{
                      $t("tasks.tasksDetails.duration")
                    }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ getDuration }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
              <v-col cols="6">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title
                      >{{ $t("tasks.tasksDetails.priority") }}
                    </v-list-item-title>
                    <v-rating v-model="editedItem.priority" length="4" readonly>
                      <template v-slot:item="props">
                        <v-icon
                          @click="props.click"
                          :color="props.isFilled ? 'primary' : ''"
                          v-text="`mdi-numeric-${props.index + 1}-box`"
                        ></v-icon>
                      </template>
                    </v-rating>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
            </v-row>
            <v-row no-gutters v-if="canSeeEstimatedCost" class="mb-4">
              <v-col cols="6">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>{{
                      $t("tasks.tasksDetails.estimatedCost")
                    }}</v-list-item-title>
                    <v-list-item-subtitle>
                      <span>{{ editedItem.estimated_cost }}</span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="12">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title
                      >{{
                        $t("tasks.tasksDetails.description")
                      }}:</v-list-item-title
                    >
                    <v-card-text
                      class="ml-0 pl-0 pt-0 body-2 grey--text text--darken-2"
                    >
                      {{ editedItem.description }}
                    </v-card-text>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <template v-for="(checklist, index) in editedItem.checklists">
                  <checklist-details
                    :key="index"
                    :checklist-index="index"
                    :checklist-length="editedItem.checklists.length"
                    :checklist="checklist"
                    @update:subtask-comment="updateSubtaskComment"
                    :content-saved.sync="contentSaved"
                    :shared="false"
                    :disabled="checkFinishedTask"
                  ></checklist-details>
                </template>
              </v-col>
            </v-row>
            <v-row no-gutters v-if="editedItem.attachments.length">
              <v-col cols="12">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title
                      v-if="editedItem.attachments.length"
                      class="mb-1"
                      >{{
                        $t("tasks.tasksDetails.attachments")
                      }}</v-list-item-title
                    >
                    <v-list-item-action-text>
                      <template
                        v-for="(attachment, index) in editedItem.attachments"
                      >
                        <v-row :key="index">
                          <v-col cols="10">
                            <v-btn @click="openAttachment(attachment)" text
                              ><v-icon color="grey darken-2"
                                >mdi-file-image</v-icon
                              >
                              <v-list-item-subtitle
                                class="text-none subtitle-2 ml-2"
                                >{{ attachment.title }}</v-list-item-subtitle
                              ></v-btn
                            >
                          </v-col>
                        </v-row>
                      </template>
                    </v-list-item-action-text>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <template>
              <v-card-subtitle class="ml-1">
                <v-progress-circular
                  :size="20"
                  :width="3"
                  indeterminate
                  color="primary"
                  v-if="!contentSaved"
                >
                </v-progress-circular>
                <span class="ml-2" v-if="!contentSaved">{{
                  $t("tasks.tasksDetails.savingChanges")
                }}</span>
                <span class="ml-2" v-if="contentSaved">{{
                  $t("tasks.tasksDetails.allChangesSaved")
                }}</span>
              </v-card-subtitle>
              <v-spacer></v-spacer>
              <v-btn color="primary" class="mr-4" text @click="close">{{
                $t("tasks.tasksDetails.cancel")
              }}</v-btn>
              <v-btn
                v-if="editedItem.status !== 'COMPLETED'"
                @click="uploadDialog"
                color="primary"
                text
                >{{ $t("tasks.tasksDetails.uploadImage") }}</v-btn
              >
              <v-btn
                v-if="editedItem.status !== 'COMPLETED'"
                @click="finish"
                color="primary"
                text
                >{{ $t("tasks.tasksDetails.finishTask") }}</v-btn
              >
            </template>
          </v-card-actions>
        </v-card>
      </v-layout>
      <delete-dialog
        :dialog="deleteDialog"
        :item="taskName"
        @agree="remove"
        v-show="false"
      />
      <upload-tasks-file-dialog
        :dialog="uploadImageDialog"
        @close="uploadImageDialog.active = false"
        @added="handleUploadImageDialog"
        v-if="uploadImageDialog.active"
      ></upload-tasks-file-dialog>
      <warning-dialog
        :dialog="finishTaskDialog.active"
        :text="finishTaskDialog.text"
        :title="finishTaskDialog.title"
        @agree="handleFinishDialogOutput"
        :confirm="$t('tasks.tasksDetails.yes')"
        :reject="$t('tasks.tasksDetails.cancel')"
        v-show="false"
      />
      <help-dialog
        :dialog="helpDialog"
        @close="closeHelpDialog"
        v-if="helpDialog.active"
      ></help-dialog>
    </v-container>
  </v-content>
</template>

<script>
import api from "@/utils/api.js";
import snackbar from "@/utils/snackbar";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import UploadTasksFileDialog from "@/components/dialogs/UploadTasksFileDialog";
import WarningDialog from "@/components/dialogs/WarningDialog.vue";
import { mapGetters, mapState } from "vuex";
import HelpDialog from "@/components/dialogs/HelpDialog";
import constraints from "@/utils/constraints";
import ChecklistDetails from "@/components/checklists/ChecklistDetails.vue";
import restrictions from "@/utils/restrictions";
import i18n from "@/utils/translations";

export default {
  components: {
    DeleteDialog,
    UploadTasksFileDialog,
    WarningDialog,
    HelpDialog,
    ChecklistDetails,
  },
  data: () => ({
    // isTaskEditedWatcher: function() {},
    // isTaskEdited: false,
    // unsavedContent: { active: false, route: null },
    canUseKnowledgeBaseBtn: false,
    helpDialog: { location: null, active: false },
    finishTaskDialog: { active: false, title: "", text: "" },
    uploadImageDialog: { active: false },
    deleteDialog: false,
    taskName: "",
    statusOptions: [],
    durations: [],
    loading: true,
    contentSaved: true,
    editedItem: {
      assignee: {},
      crm_partner_uuid: null,
      reports: [
        { engine_seconds_requested: "", crm_service: { short_task: "" } },
      ],
      subtasks: [],
      status: "",
    },
  }),

  beforeRouteEnter(to, from, next) {
    if (from.name === "notifications") {
      //This is not ideal but it works for now
      api
        .getTask(to.params.id)
        .then((resp) => {
          next();
        })
        .catch((err) => {
          if (err.response.status === 404) {
            snackbar.error(
              i18n.t("utils.snackbar.error.theRequestedTaskDoesNotExistAnymore")
            );
          } else {
            next();
          }
        });
    } else {
      next();
    }
  },

  computed: {
    ...mapGetters(["getIframeStatus"]),
    getStatus() {
      return this.statusOptions.find(
        (item) => item.status === this.editedItem.status
      ).name;
    },
    checkFinishedTask() {
      return !!this.editedItem.completed;
    },
    getDuration() {
      let duration = this.durations.find(
        (item) => item.value === this.editedItem.duration
      );
      return typeof duration === "undefined"
        ? this.editedItem.duration
        : duration.duration;
    },
    canSeeEstimatedCost: () => {
      return !!restrictions.canUseCrmFeature("can_see_estimated_cost");
    },
  },

  created() {
    this.setInitalData();
    this.canUseKnowledgeBaseBtn = constraints.canUseKnowledgeBaseBtn("tasks");
    api.getTask(this.$route.params.id).then((resp) => {
      this.editedItem = resp.data;
      this.editedItem.estimated_cost = isNaN(
        parseFloat(this.editedItem.estimated_cost)
      )
        ? null
        : parseFloat(this.editedItem.estimated_cost);

      this.editedItem.checklists.forEach((checklist) => {
        checklist.subtasks = checklist.subtasks.map((subtask) => {
          return {
            ...subtask,
            active: !!(subtask.comment && subtask.comment.length !== 0),
          };
        });
      });
      this.editedItem.priority = Number(this.editedItem.priority);
      this.loading = false;
    });
  },

  methods: {
    setInitalData() {
      this.statusOptions = [
        {
          status: "NOT_STARTED",
          name: this.$t("tasks.tasksDetails.notStarted"),
        },
        {
          status: "IN_PROGRESS",
          name: this.$t("tasks.tasksDetails.inProgress"),
        },
        { status: "COMPLETED", name: this.$t("tasks.tasksDetails.completed") },
      ];

      //TODO translations
      this.durations = [
        { duration: "15m", value: 15 },
        { duration: "30m", value: 30 },
        { duration: "45m", value: 45 },
        { duration: "1h", value: 60 },
        { duration: "2h", value: 120 },
        { duration: "4h", value: 240 },
        { duration: "8h", value: 480 },
        { duration: "12h", value: 720 },
        { duration: "16h", value: 960 },
        { duration: "24h", value: 1440 },
      ];
    },
    closeHelpDialog(event) {
      if (event === "close") {
        this.helpDialog.active = false;
      }
    },
    handleHelp(event) {
      this.helpDialog.active = true;
      this.helpDialog.location = "tasks";
    },
    updateSubtaskComment(subtask) {
      let index = this.editedItem.checklists[subtask.index].subtasks.findIndex(
        (element) => subtask.uuid === element.uuid
      );
      if (!subtask.active) {
        this.$set(
          this.editedItem.checklists[subtask.index].subtasks[index],
          "active",
          true
        );
      } else {
        this.$set(
          this.editedItem.checklists[subtask.index].subtasks[index],
          "active",
          false
        );
      }
    },
    openAttachment(attachment) {
      !this.getIframeStatus
        ? window.open(attachment.file_link, "_blank")
        : window.open(attachment.file_link, "_self");
    },
    handleUploadImageDialog(event) {
      event.forEach((element) => {
        this.editedItem.attachments.push(element);
      });
      this.uploadImageDialog.active = false;
    },

    uploadDialog() {
      this.uploadImageDialog.active = true;
    },

    close() {
      this.$router.push({
        name: "tasks",
      });
    },

    edit() {
      this.$router.push({
        name: "tasksEdit",
        params: { id: this.$route.params.id },
      });
    },

    finish() {
      this.finishTaskDialog.active = true;
      this.finishTaskDialog.title = this.$t(
        "tasks.tasksDetails.areYouSureYouWantToFinishTheTask"
      );
    },

    handleFinishDialogOutput(status) {
      if (status) {
        api
          .finishTask(this.editedItem.uuid)
          .then((resp) => {
            snackbar.success(
              this.$t("utils.snackbar.success.successfullySaved")
            );
            this.close();
          })
          .catch((err) => {
            this.handleError(err.response);
          });
      }
      this.finishTaskDialog.active = false;
    },

    handleError(err) {
      if (err) {
        snackbar.error(err.response);
      } else {
        snackbar.error();
      }
    },

    activateDeleteDialog() {
      this.deleteDialog = true;
      this.taskName = "task " + this.editedItem.name;
    },

    remove(agreed) {
      if (agreed) {
        api.deleteTask(this.editedItem.uuid).then((resp) => {
          snackbar.success(
            this.$t("utils.snackbar.success.successfullyDeleted")
          );
          this.close();
        });
        // .catch((err) => {
        //   snackbar.error();
        // });
      }
      this.deleteDialog = false;
    },
  },
};
</script>
<style scoped>
.comment--style {
  white-space: normal;
  height: 120px;
  overflow-y: auto;
}
</style>
